import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from '../../../../hooks';
import proofkitFilemakerService from '../../../../services/proofkitFilemakerService';
import { ArrowLeft, Edit, Trash2, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Label } from '../../../../components/ui';

const SEGMENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_SEGMENTS || "segments";
const CONTACT_SEGMENTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACT_SEGMENTS || "contact_segments";

export default function SegmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [segment, setSegment] = useState(null);
  const [contactsCount, setContactsCount] = useState(0);

  useEffect(() => {
    fetchSegment();
  }, [id]);

  const fetchSegment = async () => {
    try {
      setLoading(true);
      const res = await proofkitFilemakerService.getRecord(SEGMENTS_LAYOUT, id);
      const fieldData = res?.data?.fieldData || {};
      
      const segmentId = fieldData?.ClavePrincipal || res?.data?.recordId || id;

      setSegment({
        id: segmentId,
        nombre: fieldData.nombre || "",
        descripcion: fieldData.descripcion || "",
        activo: Number(fieldData.activo ?? 1),
      });

      const contactSegmentsRes = await proofkitFilemakerService.getAllRecords(CONTACT_SEGMENTS_LAYOUT);
      const rows = Array.isArray(contactSegmentsRes?.data) ? contactSegmentsRes.data : [];
      const count = rows.reduce((acc, row) => {
        const fd = row?.fieldData || {};
        const rowSegmentId = fd?.segment_id;
        if (rowSegmentId && String(rowSegmentId) === String(segmentId)) return acc + 1;
        return acc;
      }, 0);
      setContactsCount(count);
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo cargar el segmento",
        variant: "destructive",
      });
      navigate("/campanas/segments");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Estás seguro de eliminar este segmento?")) return;
    
    try {
      await proofkitFilemakerService.deleteRecord(SEGMENTS_LAYOUT, id);
      toast({
        title: "Éxito",
        description: "Segmento eliminado correctamente",
      });
      navigate("/campanas/segments");
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el segmento",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Cargando segmento...</div>
      </div>
    );
  }

  if (!segment) return null;

  const getActivoBadge = (activo) => {
    const v = Number(activo);
    if (v === 1) return <Badge variant="default">Activo</Badge>;
    if (v === 0) return <Badge variant="secondary">Inactivo</Badge>;
    return <Badge variant="secondary">{String(activo ?? "-")}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate("/campanas/segments")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{segment.nombre}</h1>
            <p className="text-muted-foreground">{segment.descripcion}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/campanas/segments/${id}/edit`)}>
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4 mr-2" />
            Eliminar
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Información General</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <p className="text-sm">{segment.nombre}</p>
            </div>
            <div>
              <Label>Descripción</Label>
              <p className="text-sm">{segment.descripcion || "Sin descripción"}</p>
            </div>
            <div>
              <Label>Activo</Label>
              <div className="mt-1">{getActivoBadge(segment.activo)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Estadísticas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  <span>Total de Contactos</span>
                </div>
                <Badge variant="outline">{contactsCount}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
